import { useWidgetData } from 'adapter/in/common/widget/widgetHooks';
import { getFieldsContainers } from 'adapter/in/common/form/renderer';
import {
    populateAddWidgetForm,
    populateEditDataWidgetForm,
    populateEditOptionsWidgetForm,
} from 'adapter/in/dashboards/containers/EditDashboard/WidgetMenu/Panel/Configuration/Tabs/Common';
import {
    getOptionsFormByWidgetType,
    validateWidgetIdAndGetOptionsContainers,
} from 'adapter/in/dashboards/containers/EditDashboard/WidgetMenu/Panel/Configuration/Tabs/Options';
import { useCurrentWidgetConfiguration } from 'adapter/in/common/context/currentWidgetConfiguration';
import { useDashboardEdit } from 'adapter/in/dashboards/context/EditDashboard';
import { dispatchWidgetMenuView, useWidgetMenuView, WIDGET_STATUS } from 'adapter/in/dashboards/context/EditDashboard/WidgetMenuView';
import useWidget from 'adapter/in/dashboards/context/Widget/data';
import { usePrevious } from 'adapter/in/common/hooks/usePrevious';
import { ErrorData } from 'domain/types';
import { isSome, Option } from 'fp-ts/Option';
import { ReactElement, useEffect, useMemo, useState } from 'react';
import { FieldValues, useForm, UseFormReturn } from 'react-hook-form';
import { ReadPresetFormCriteriaBuilder } from 'use-case/presets/form/readPresetFormCriteria';
import { ReadWidgetCriteriaBuilder } from 'use-case/widget/read/readWidgetCriteria';
import { ReadWidgetDataCriteriaBuilder } from 'use-case/widgets/data/read/readWidgetDataCriteria';
import { useFormChangesView } from 'adapter/in/common/context/formChanges';
import { useCommonDependencies } from 'adapter/in/common/commonDependencies';
import { ConfigFieldType, FieldStatus } from 'adapter/in/common/form/types';
import OptionsFormField, { OptionFieldKey } from 'domain/entity/options/optionFormField';
import { buildSelectorFieldData } from 'adapter/in/common/hooks/form/useBaseSelectorContainer';
import { ABSOLUTE_PERCENTAGE_VALUES } from 'adapter/in/common/widget/options';
import SingleKPIData from 'domain/entity/widgets/SingleKPIData';
import {
    usePresetFormUseCase,
    useWidgetUseCase,
} from 'adapter/in/dashboards/containers/EditDashboard/WidgetMenu/Panel/Configuration/Tabs/Common/sideMenuhooks';

const useSideMenuFormContainer = (): {
    form: UseFormReturn<FieldValues>;
    tab: ConfigFieldType;
    fields: ReactElement[];
    options: ReactElement[];
    error: Option<ErrorData>;
    errorAction?: () => void;
    isTabWithError: (currentTab: string) => boolean;
    featureName: string;
    presetName: string;
    isLoading: boolean;
} => {
    const form = useForm();
    const {
        getValues,
        setValue,
        reset,
        formState: { isValid, errors },
    } = form;
    const [hasError, setHasError] = useState(false);
    const { hasChange } = useFormChangesView();
    const { readPresetForm: readPresetFormUsecase } = useCommonDependencies();
    const { readWidget: readWidgetUsecase, readWidgetData: readWidgetDataUseCase } = useWidget();
    const { status, tab } = useWidgetMenuView();
    const { feature, preset, widgetType, widgetId } = useCurrentWidgetConfiguration();
    const prevWidgetId = usePrevious(widgetId);
    const prevTab = usePrevious(tab);
    const { setDisableSaveButtonTo, toggleSaveButton } = dispatchWidgetMenuView();
    const { dashboard } = useDashboardEdit();
    const { isLoadingPresetForm, errorPresetForm, dataPresetForm, readPresetForm } = usePresetFormUseCase(readPresetFormUsecase);
    const { isLoadingReadWidget, errorReadWidget, dataReadWidget, readWidget } = useWidgetUseCase(readWidgetUsecase);
    const { isWidgetDataLoading, widgetData, widgetDataError: hasWidgetDataError, readWidgetData, reset: resetDataWidget } = useWidgetData(
        readWidgetDataUseCase
    );

    const isEditing = status === WIDGET_STATUS.EDITING;

    const optionsForm = useMemo(() => getOptionsFormByWidgetType(widgetType), [widgetType]);

    const errorAction = useMemo(() => () => readPresetForm(ReadPresetFormCriteriaBuilder.create().withPreset(preset.key).build()), [
        preset,
    ]);

    const isTabWithError = (currentTab: string) => {
        const hasInvalidFields = Object.values(getValues())
            .filter((value) => value.type === currentTab)
            .map((value) => value.status)
            .some((fieldStatus) => fieldStatus === FieldStatus.INVALID);
        if ((!hasError || Object.keys(errors).length === 0) && !hasInvalidFields) return false;
        return (
            Object.keys(errors).filter((key) => getValues(key).type === currentTab && errors[key]?.type !== 'loading').length !== 0 ||
            hasInvalidFields
        );
    };

    useEffect(() => {
        if (
            isSome(errorReadWidget) ||
            isSome(errorPresetForm) ||
            isLoadingPresetForm ||
            isLoadingReadWidget ||
            (isEditing && (isSome(hasWidgetDataError) || isWidgetDataLoading))
        ) {
            toggleSaveButton(false);
        } else {
            toggleSaveButton(true);
        }
    }, [errorPresetForm, errorReadWidget, isLoadingReadWidget, isLoadingPresetForm, isEditing, hasWidgetDataError, isWidgetDataLoading]);

    useEffect(() => {
        if (!isValid && hasChange) {
            setHasError(true);
        } else if (isValid && hasChange) {
            setHasError(false);
        }
    }, [isValid, hasChange]);

    useEffect(() => {
        if (tab === ConfigFieldType.DATA) {
            setDisableSaveButtonTo(!isValid);
        }
    }, [tab, isValid]);

    const cleanUp = () => {
        setHasError(false);
    };

    useEffect(() => {
        if (!isEditing || (isEditing && widgetId !== 'NONE' && tab === ConfigFieldType.DATA)) {
            readPresetForm(ReadPresetFormCriteriaBuilder.create().withPreset(preset.key).build());
        }
    }, [widgetId, isEditing, tab]);

    useEffect(() => {
        if (isEditing && widgetId !== 'NONE' && isSome(dashboard)) {
            readWidget(ReadWidgetCriteriaBuilder.create().withWidgetId(widgetId).build());
        }
    }, [widgetId, isEditing, dashboard, tab]);

    useEffect(() => {
        if (widgetId && tab === ConfigFieldType.OPTIONS) {
            readWidgetData(ReadWidgetDataCriteriaBuilder.create().withWidgetId(widgetId).build());
        }
    }, [widgetId, dashboard, tab]);

    useEffect(() => {
        if (prevWidgetId !== widgetId || prevTab !== tab) {
            cleanUp();
            reset();
            resetDataWidget();
        } else if (!isEditing && isSome(dataPresetForm) && prevWidgetId === widgetId) {
            populateAddWidgetForm(dataPresetForm.value.fields, setValue);
        } else if (isEditing && prevWidgetId === widgetId) {
            if (isSome(dataReadWidget) && isSome(dataPresetForm) && prevTab === ConfigFieldType.DATA) {
                populateEditDataWidgetForm(dataReadWidget.value, widgetType, dataPresetForm.value.fields, setValue);
            } else if (isSome(dataReadWidget) && isSome(widgetData) && prevTab === ConfigFieldType.OPTIONS) {
                populateEditOptionsWidgetForm(dataReadWidget.value, widgetType, widgetData.value, setValue);
            }
        }
    }, [dataReadWidget, dataPresetForm, widgetData, widgetId, prevWidgetId, tab, widgetType]);

    // This UseEffect can be used to set defaults on Options Form when they depend on Data
    useEffect(() => {
        if (
            widgetId === prevWidgetId &&
            isSome(dataReadWidget) &&
            isSome(widgetData) &&
            dataReadWidget.value.optionFields.every((optionField) => optionField.key !== OptionFieldKey.COMPARISON_DISPLAY_MODE) &&
            optionsForm.some((formField: OptionsFormField) => formField.key === OptionFieldKey.COMPARISON_DISPLAY_MODE)
        ) {
            const defaultValue = (widgetData.value as SingleKPIData).comparison.showAsPercentage
                ? ABSOLUTE_PERCENTAGE_VALUES.PERCENTAGE
                : ABSOLUTE_PERCENTAGE_VALUES.ABSOLUTE;
            setValue('absolute_percentage', buildSelectorFieldData(FieldStatus.INITIAL_VALUE, defaultValue, ConfigFieldType.OPTIONS));
        }
    }, [dataReadWidget, widgetData, optionsForm, widgetId, prevWidgetId]);

    return {
        form,
        tab: tab as ConfigFieldType,
        fields:
            isSome(dataPresetForm) && prevTab === ConfigFieldType.DATA
                ? getFieldsContainers(dataPresetForm.value.fields, ConfigFieldType.DATA)
                : [],
        options:
            isSome(widgetData) && isEditing && prevTab === ConfigFieldType.OPTIONS
                ? validateWidgetIdAndGetOptionsContainers(optionsForm, widgetData.value, widgetId, prevWidgetId)
                : [],
        isTabWithError,
        isLoading: isLoadingPresetForm || isLoadingReadWidget || isWidgetDataLoading,
        error: errorPresetForm || errorReadWidget || hasWidgetDataError,
        errorAction: isSome(errorPresetForm) ? errorAction : undefined,
        featureName: feature.name,
        presetName: preset.name,
    };
};

export default useSideMenuFormContainer;
